import { FC } from 'react';

import { Head } from '@inertiajs/react';
import { HandCoins, ShoppingCart, Store } from 'lucide-react';

import { buyerSteps, sellerSteps } from './Faq/faqContent';
import { FaqJourney } from './Faq/FaqJourney';
import { FaqGroup, FaqQuestionGroup } from './Faq/FaqQuestionGroup';
import { FaqRoleSelector } from './Faq/FaqRoleSelector';
import { FaqSupport } from './Faq/FaqSupport';

type Props = {
    faqGroups: FaqGroup[];
};

const groupById = (groups: FaqGroup[], id: string) =>
    groups.find((group) => group.id === id);

const Faq: FC<Props> = ({ faqGroups }) => {
    const buyerQuestions = groupById(faqGroups, 'buyers');
    const sellerQuestions = groupById(faqGroups, 'sellers');
    const commonQuestions = groupById(faqGroups, 'common');

    return (
        <>
            <Head title="Часті запитання (FAQ) | CartOn" />
            <main className="container mx-auto max-w-5xl py-8 sm:py-12">
                <header className="max-w-3xl">
                    <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
                        Як працює CartOn
                    </h1>
                    <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                        Оберіть свою роль і пройдіть шлях від першого кроку до
                        завершеного замовлення. Нижче — короткі інструкції та
                        відповіді без складних формулювань.
                    </p>
                </header>

                <aside className="mt-6 max-w-3xl border-l-2 border-border pl-4">
                    <h2 className="text-sm font-semibold">Важливо</h2>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground sm:text-base">
                        CartOn допомагає знайти товар, оформити замовлення та
                        стежити за його статусом, але не приймає оплату за товар
                        і не доставляє його. Внутрішній баланс використовується
                        лише для сервісних зборів продавця.
                    </p>
                </aside>

                <FaqRoleSelector />

                <div className="mt-16 space-y-16">
                    <div className="space-y-10">
                        <FaqJourney
                            id="buyers"
                            variant="buyer"
                            icon={<ShoppingCart className="h-5 w-5" />}
                            eyebrow="Покупцям"
                            title="Як купити товар"
                            description="Чотири кроки від пошуку потрібної пропозиції до відгуку про покупку."
                            steps={buyerSteps}
                        />
                        {buyerQuestions && (
                            <FaqQuestionGroup group={buyerQuestions} />
                        )}
                    </div>

                    <div className="space-y-10">
                        <FaqJourney
                            id="sellers"
                            variant="seller"
                            icon={<Store className="h-5 w-5" />}
                            eyebrow="Продавцям"
                            title="Як продати товар"
                            description="Підготуйте профіль і пропозицію, а потім ведіть замовлення за зрозумілими статусами."
                            steps={sellerSteps}
                        />
                        {sellerQuestions && (
                            <FaqQuestionGroup group={sellerQuestions} />
                        )}
                    </div>

                    {commonQuestions && (
                        <section
                            id="common"
                            className="scroll-mt-24 rounded-3xl bg-muted/40 p-5 sm:p-8"
                        >
                            <div className="mb-6 flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-background text-primary shadow-sm">
                                    <HandCoins className="h-5 w-5" />
                                </div>
                                <div>
                                    <h2
                                        id="common-questions-title"
                                        className="text-2xl font-bold"
                                    >
                                        Важливо для всіх
                                    </h2>
                                    <p className="mt-1 text-sm text-muted-foreground sm:text-base">
                                        Оплата, доставка, безпека та допомога у
                                        складній ситуації.
                                    </p>
                                </div>
                            </div>
                            <FaqQuestionGroup group={commonQuestions} compact />
                        </section>
                    )}
                </div>

                <FaqSupport />
            </main>
        </>
    );
};

export default Faq;
